import { useMutation, useQuery } from '@apollo/client';
import { ExternalFinancialEntity } from '../../financialEntities';
import {
    AllFinancialEntitiesDocument,
    DeleteFinancialEntityDocument,
    FinancialEntityDeleteCheckDocument,
} from '../../graphql/generated';
import { useIsActiveFundingAccount } from './useIsActiveFundingAccount';

export const useDeleteFinancialEntity = ({
    financialEntity,
    onComplete,
}: {
    financialEntity: ExternalFinancialEntity;
    onComplete: VoidFunction;
}):
    | { isLoading: true }
    | { isLoading: false; isAlreadyDeleted: true }
    | {
          isLoading: false;
          isAlreadyDeleted: false;
          title: string;
          description: string;
          deleteFinancialEntity?: VoidFunction;
          isSubmitting?: boolean;
      } => {
    const isActiveFundingAccount = useIsActiveFundingAccount(financialEntity);
    const { data: deleteCheckData } = useQuery(
        FinancialEntityDeleteCheckDocument,
        {
            variables: { id: financialEntity.id },
            fetchPolicy: 'no-cache',
        }
    );
    const deleteCheck = deleteCheckData?.financialEntity?.deleteCheck;

    const [deleteFinancialEntity, { loading: isSubmitting }] = useMutation(
        DeleteFinancialEntityDocument,
        {
            variables: { id: financialEntity.id },
            refetchQueries: [AllFinancialEntitiesDocument],
            onCompleted: () => {
                onComplete();
            },
        }
    );

    if (!deleteCheck) return { isLoading: true };

    if (deleteCheck === 'ALREADY_DELETED') {
        return { isLoading: false, isAlreadyDeleted: true };
    }

    if (deleteCheck === 'PENDING_TRANSACTIONS') {
        return {
            isLoading: false,
            isAlreadyDeleted: false,
            title: 'Unable to delete account',
            description:
                'This account has a pending transfer. Please wait for the transfer to complete and then try again.',
        };
    }

    // deleteCheck === FinancialEntityDeleteCheck.CanBeDeleted
    return {
        isLoading: false,
        isAlreadyDeleted: false,
        title: 'Are you sure you want to delete this account?',
        description: isActiveFundingAccount
            ? 'This account is currently funding one or more goals. Deleting this account will disconnect the following recurring transfers:'
            : 'It will no longer be visible, and will not contribute to your net worth.',
        deleteFinancialEntity,
        isSubmitting,
    };
};
